import { isNil } from 'lodash-es';
import { type PropsWithChildren } from 'react';

import { Flex, Grid } from '@dynatrace/strato-components/layouts';

import { GlobalFontSizeVariables } from './components/GlobalFontSizeVariables.js';
import { SingleValueDisplay } from './components/SingleValueDisplay.js';
import { SINGLE_VALUE_GRID_ARIA_LABEL } from './defaults.js';
import { useLabelCount } from './hooks/use-label-count.js';
import { GlobalFontSizeProvider } from './providers/global-font-size-provider.js';
import { gridRows } from './SingleValueGrid.sty.js';
import { Sparkline } from './slots/Sparkline.js';
import { Trend } from './slots/Trend.js';
import { Value } from './slots/Value.js';
import type { SingleValueGridAccessorsConfig } from './types/single-value-grid.js';
import { calculateGridLayout } from './utils/calculate-grid-layout.js';
import { iterateSlots } from './utils/iterate-slots.js';
import type { Threshold } from '../../core/types/threshold-types.js';
import type { SingleValueAlignment } from '../core/components/single-value-renderer/types/single-value-base-props.js';
import { useThrottledResizeObserver } from '../core/hooks/useThrottledResizeObserver.js';

/**
 * Props for the SingleValueGrid component.
 * @public
 */
export interface SingleValueGridProps {
  /** The data source for the SingleValueGrid component. */
  data: (number | string | Record<string, unknown>)[];
  /**
   * The default color for the SingleValues.
   * This color will be overwritten by individual colors specified in the SingleValueGridData or thresholds prop.
   */
  color?: string;
  /** Accessor function to override colors for individual SingleValue components. */
  colorAccessor?: string;
  /** This option will define if the color is applied to the value or to the background. */
  applyThresholdBackground?: boolean;
  /**
   * Threshold configurations that define conditions to match a given value.
   * These configurations will overwrite the color prop for the corresponding SingleValue components.
   */
  thresholds?: Threshold[];
  /** Accessor function for individual labels of each SingleValue component. */
  labelAccessor?: string;
  /** Accessor function for individual prefix icons of each SingleValue component. */
  prefixIconAccessor?: string;
  /**
   * Horizontal alignment of icons, values, units, labels, and trends within each SingleValue component.
   * @defaultValue 'start'
   */
  alignment?: SingleValueAlignment;
}

const SingleValueGridRoot = (
  props: PropsWithChildren<SingleValueGridProps>,
) => {
  const {
    data,
    children,
    colorAccessor,
    labelAccessor,
    prefixIconAccessor,
    applyThresholdBackground,
    ...gridConfig
  } = props;

  const labelCount = useLabelCount(data, labelAccessor);

  const { accessors: slotAccessors, ...slotsConfig } = iterateSlots(children);

  const trendCount = slotsConfig.trend !== undefined ? data.length : 0;

  const accessors: SingleValueGridAccessorsConfig = {
    colorAccessor,
    labelAccessor,
    prefixIconAccessor,
    ...slotAccessors,
  };

  const enableThresholdBackground =
    applyThresholdBackground &&
    !isNil(gridConfig.thresholds) &&
    gridConfig.thresholds?.length > 0;

  const propsConfig = {
    ...gridConfig,
    ...slotsConfig,
    applyThresholdBackground: enableThresholdBackground,
  };

  const { height = 0, width = 0, refCallback } = useThrottledResizeObserver();

  const { rowCount, columnCount, lastRowColumnCount, gap } =
    calculateGridLayout(width, height, data.length);

  const gridItems = data.slice(0, data.length - lastRowColumnCount);
  const lastRowItems = data.slice(-lastRowColumnCount);

  return (
    <GlobalFontSizeProvider
      valueCount={data.length}
      labelCount={labelCount}
      trendCount={trendCount}
    >
      <GlobalFontSizeVariables>
        <Grid
          className={gridRows}
          ref={refCallback}
          gridTemplateRows={`repeat(${rowCount}, 1fr)`}
          gridTemplateColumns={`repeat(${columnCount}, 1fr)`}
          style={{
            // Explicitly set this one via the style, as the `gridGap` settable via the
            // props on the `Grid` component needs to be a valid spacing token
            gridGap: gap,
          }}
          role={'img'}
          aria-label={SINGLE_VALUE_GRID_ARIA_LABEL}
        >
          {gridItems.map((record, index) => (
            <SingleValueDisplay
              key={index}
              record={record}
              propsConfig={propsConfig}
              accessors={accessors}
            />
          ))}
          <Flex
            className={gridRows}
            style={{
              // Explicitly set this one via the style, as the `gridGap` settable via the
              // props on the `Grid` component needs to be a valid spacing token
              columnGap: gap,
            }}
            gridColumn={'1 / -1'}
          >
            {lastRowItems.map((record, index) => (
              <SingleValueDisplay
                key={index}
                record={record}
                propsConfig={propsConfig}
                accessors={accessors}
              />
            ))}
          </Flex>
        </Grid>
      </GlobalFontSizeVariables>
    </GlobalFontSizeProvider>
  );
};

SingleValueGridRoot['displayName'] = 'SingleValueGrid';

/**
 * The SingleValueGrid component
 * @public
 */
export const SingleValueGrid = Object.assign(SingleValueGridRoot, {
  Value,
  Trend,
  Sparkline,
});
